import { querySpecSchema, type QuerySpec } from '@console/types'

/**
 * The `<result_id>` in `/ask/result/<result_id>` (spec §7).
 *
 * The spec calls this "a stable hash of the query spec", and requires the URL
 * to be bookmarkable, shareable, and refreshable — refreshing re-runs the
 * query against current data. A one-way hash cannot deliver all four without
 * somewhere to look the spec back up, which would mean a server-side table
 * of every query anyone has ever run.
 *
 * So this encodes the spec rather than digesting it: URL-safe base64 of the
 * canonical JSON. It has every property the spec asks for — the same query
 * always produces the same id (stable), and any holder of the URL can re-run
 * it (shareable, refreshable) without the console storing anything.
 *
 * The trade is that the id is long and the query is legible to anyone with
 * the link. Neither costs anything here: a query spec contains only what the
 * operator typed a question about, never results, and it is authorised
 * server-side per request anyway — a shared link runs against the *recipient's*
 * own tenant, and cannot carry another tenant's data.
 */

/** Key order is normalised so two identical queries can never produce two ids. */
function canonicalise(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalise)
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
    return Object.fromEntries(entries.map(([k, v]) => [k, canonicalise(v)]))
  }
  return value
}

function toUrlSafe(base64: string): string {
  return base64.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function fromUrlSafe(value: string): string {
  const restored = value.replaceAll('-', '+').replaceAll('_', '/')
  return restored.padEnd(restored.length + ((4 - (restored.length % 4)) % 4), '=')
}

export function encodeQuerySpec(spec: QuerySpec): string {
  const json = JSON.stringify(canonicalise(spec))
  // `unescape(encodeURIComponent(...))` is the standard dance for getting
  // UTF-8 through btoa, which is Latin-1 only. A customer name with a
  // non-ASCII character would otherwise throw here rather than at any point
  // that would tell you why.
  const bytes = new TextEncoder().encode(json)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return toUrlSafe(btoa(binary))
}

/**
 * Returns null rather than throwing: a hand-edited or truncated URL is a
 * thing that happens, and the result page should say "that link isn't a
 * query" rather than crash.
 */
export function decodeQuerySpec(id: string): QuerySpec | null {
  try {
    const binary = atob(fromUrlSafe(id))
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    const result = querySpecSchema.safeParse(parsed)
    return result.success ? result.data : null
  } catch {
    return null
  }
}
